<template>
  <ListModal @cancel="closeModal" @confirmed="confirmModal" v-if="show">
    <template v-slot:default>
      <tr class="details-table__row details-table__row--vehicle">
        <td class="details-table__col details-table__col--vehicle">
          <h2 class="change-plate-title">{{ $t('add_permit.change_plate') }}</h2>
          <div class="plateSearchWrapper">
            <el-input
              id="plateSearch"
              ref="plateSearchInput"
              v-model="plateSearch"
              :placeholder="$t('add_permit.search_plate')"
              v-validate="'max:15'"
              name="plateSearch"
              class="plateSearchInput"
              @keydown.native="stopInputAtDashAndSpace"
              @keydown.enter.native="searchPlateNumber"
            >
            </el-input>
            <el-button @click="searchPlateNumber" class="plateSearchButton">
              <i class="el-icon-search"/>
            </el-button>
            <el-button v-if="plateSearch.length > 0" @click="clearPlateSearch" class="close-search-btn" style="margin-left: 16px;">
              <i class="el-icon-close"/>
            </el-button>
          </div>
          <div v-for="vehicle in vehicles.data" :key="vehicle.id" :id="'plate-' + vehicle.id"
               :class="vehicle.id === editingVehicleId ? 'permit-edit-block active-border' : 'permit-edit-block'">
            <!-- edit permit row block -->
            <div :class="vehicle.id === selectedVehicle.id ?
              (vehicle.id === editingVehicleId ? 'row-title' : 'row-title active-plate-bg')
              : 'row-title'">
              <!-- edit icon -->
              <div v-if="vehicle.id !== editingVehicleId && !vehicle.new" class="edit-icon" @click="selectActivePlate(vehicle.id)" />
              <!-- plate number + parking icon -->
              <el-tag :key="vehicle.id" closable :disable-transitions="false"
                :class="getVehicleClass(vehicle)"
                role="select vehicle" @click.native="selectVehicle(vehicle)"
                @close.stop.prevent="removeVehicle(vehicle)">
                <div style="flex-direction: column; ">
                  <span class="vehicle-reg">{{ vehicle.vehicle_reg }}</span>
                  <span v-if="vehicle.comment && vehicle.id !== editingVehicleId" class="vehicle-comment">
                    {{ vehicle.comment }}
                  </span>
                  <span v-else-if="vehicle.id !== editingVehicleId" class="vehicle-comment no-comment">
                    &nbsp;
                  </span>
                </div>
              </el-tag>
            </div>
            <!-- comment edit form -->
            <div :id="'comment-block-' + vehicle.id" :ref="'comment-block-' + vehicle.id"
              :class="vehicle.id === editingVehicleId ? 'active-edit' : 'hidden-edit'">
              <input :ref="`comment-${vehicle.id}`" class="vehicleInput-comment" :placeholder="$t('parking.comment')"
                :value="vehicle.comment">
              <!-- save/cancel buttons -->
              <div class="form__buttons buttonset">
                <div class="buttonset__button btn btn--secondary btn--small" @click="selectActivePlate(0)">
                  {{ $t('common.cancel') }}
                </div>
                <button :ref="`confirm-btn-${vehicle.id}`" type="submit"
                  class="buttonset__button btn btn--main btn--small" @click.prevent.stop="saveComment(vehicle.id)">
                  {{ $t('common.confirm') }}
                </button>
              </div>
            </div>
          </div>
          <div class="vehicle-count">
            <div class="vehicle-count--count">{{ $t('add_permit.shown_x_of_y', { x: vehicles.data ? vehicles.data.length : 0, y: vehicles.total }) }}</div>
            <button
              type="submit"
              class="buttonset__button btn btn--secondary"
              v-if="vehicles.data && vehicles.data.length < vehicles.total"
              @click.prevent.stop="fetchMorePlates()"
            >
              {{ $t('add_permit.show_next_x', { x: 10 }) }}
            </button>
          </div>
           <!-- new plate number form -->
            <el-input v-if="!vehicleInputDisabled" id="vehicleReg" ref="vehicleInput" v-model="form.vehicleReg"
              v-validate="'max:15'" name="vehicleReg" class="vehicleInput-toggle"
              :class="vehicleRegError ? 'vehicleInput-toggle--error' : ''" @keyup.enter.native="addVehicleReg()"
              @keydown.native="stopInputAtDashAndSpace" @focus="vehicleRegError = false"
              @input="vehicleRegError = false">


              <el-button v-if="!vehicleInputDisabled" slot="append" @click="addVehicleReg()">
                {{ $t('common.ok') }}
              </el-button>
            </el-input>
            <el-input v-if="!vehicleInputDisabled" ref="vehicleComment" v-model="form.vehicleComment"
              class="vehicleInput-comment resident" name="vehicleComment" :placeholder="$t('parking.comment')" />
            <el-button v-if="vehicleInputDisabled" class="vehicle-input" @click="vehicleInputDisabled = false">
              {{ '+ ' + $t('add_permit.add_car') }}
              <i class="el-icon-edit el-icon-right" />
            </el-button>
            <tooltip v-show="errors.has('vehicleReg:max')"
              class="form__validation to-bottom">
              {{ $tc('errors.field_max', 1,
                {
                  max: 15, have: form.vehicleReg ?
                    form.vehicleReg.length : ''
                }) }}
            </tooltip>
        </td>
      </tr>
    </template>
  </ListModal>
</template>

<script>
import ListModal from './ListModal.vue';
import Tooltip from 'common/Tooltip';
import { sanitizeVehicleRegNumber } from '../../utils';

export default {
  name: 'VehicleSelectionModal',
  components: { ListModal, Tooltip },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    userId: {
      type: [String, Number],
      required: true,
    },
    activeVehicleId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      vehicles: {
        data: [],
        total: 0,
      },
      selectedVehicle: {
        id: null,
      },
      editingVehicleId: null,
      plateSearch: '',
      vehicleLimit: 10,
      vehicleOffset: 0,
      form: {
        vehicleReg: '',
        vehicleComment: '',
      },
      vehicleInputDisabled: true,
      vehicleRegError: false,
    };
  },
  watch: {
    show(isShown) {
      if (isShown) {
        this.vehicleOffset = 0;
        this.fetchVehiclePlateNumbers();
        this.vehicleInputDisabled = true;
      }
    },
    userId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.vehicleOffset = 0;
        this.fetchVehiclePlateNumbers();
      }
    },
    activeVehicleId: {
      handler(newVal) {
        this.selectedVehicle = { id: newVal };
      },
      immediate: true,
    },
  },
  methods: {
    fetchVehiclePlateNumbers() {
      if (!this.userId) return;
      const params = new URLSearchParams();
      params.append('limit', this.vehicleLimit);
      if (this.vehicleOffset) params.append('offset', this.vehicleOffset);
      if (this.plateSearch) params.append('s', this.plateSearch);

      this.$http.get(`${process.env.BACKEND_API_URL}api/user/${this.userId}/vehicle?${params.toString()}`)
        .then((response) => {
          const newData = response.data.data;
          if (this.vehicleOffset > 0) {
            this.vehicles.data.push(...newData);
          } else {
            this.vehicles.data = newData;
          }
          this.vehicles.total = response.data.total;
        })
        .catch((error) => {
          console.error('Failed to fetch vehicles:', error);
        });
    },
    fetchMorePlates() {
      this.vehicleOffset += this.vehicleLimit;
      this.fetchVehiclePlateNumbers();
    },
    searchPlateNumber() {
      this.vehicleOffset = 0;
      this.fetchVehiclePlateNumbers();
    },
    clearPlateSearch() {
      this.plateSearch = '';
      this.vehicleOffset = 0;
      this.fetchVehiclePlateNumbers();
    },
    selectVehicle(vehicle) {
      this.selectedVehicle = vehicle;
    },
    getVehicleClass(vehicle) {
        let classes = '';
        if (vehicle.id === this.selectedVehicle.id) {
            classes += 'el-tag--success ';
        }
        if (vehicle.permits_count > 0) {
            classes += 'el-tag--permit ';
        }
        if (vehicle.new) {
            // New vehicles that are selected are green
            if(vehicle.id === this.selectedVehicle.id) {
                return 'el-tag--success';
            }
            return '';
        }
        return classes;
    },
    stopInputAtDashAndSpace(event) {
      if (event.key === '-' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    selectActivePlate(vehicleId) {
      this.editingVehicleId = vehicleId;
    },
    saveComment(vehicleId) {
      const commentInput = this.$refs[`comment-${vehicleId}`];
      if (!commentInput || !commentInput[0]) return;

      const newComment = commentInput[0].value;
      const confirmBtn = this.$refs[`confirm-btn-${vehicleId}`];
      if(confirmBtn && confirmBtn[0]) {
        confirmBtn[0].disabled = true;
      }

      this.$http.put(`${process.env.BACKEND_API_URL}api/user/${this.userId}/vehicle/${vehicleId}`, { comment: newComment })
        .then(() => {
          const vehicle = this.vehicles.data.find(v => v.id === vehicleId);
          if (vehicle) {
            vehicle.comment = newComment;
          }
          this.editingVehicleId = null;
        })
        .catch(error => {
          console.error('Failed to save comment:', error);
        })
        .finally(() => {
            if(confirmBtn && confirmBtn[0]) {
                confirmBtn[0].disabled = false;
            }
        });
    },
    removeVehicle(vehicle) {
        if (vehicle.new) {
            if (vehicle.id === this.selectedVehicle.id) {
                // In a real app, you'd show a user-facing warning.
                console.warn("Cannot remove a vehicle that is currently selected.");
                return;
            }
            const index = this.vehicles.data.findIndex(obj => obj.id === vehicle.id);
            if (index > -1) {
                this.vehicles.data.splice(index, 1);
                this.vehicles.total -= 1;
            }
            return;
        }

        if (vehicle.permits_count > 0) {
            console.warn("Cannot remove vehicle with active permits.");
            return;
        }
        this.$store.dispatch('users/vehicles/delete', { id: vehicle.id, user: this.user })
            .then(() => {
                this.vehicles.data = this.vehicles.data.filter(v => v.id !== vehicle.id);
                this.vehicles.total -= 1;
                if (this.selectedVehicle.id === vehicle.id) {
                    this.selectedVehicle = { id: null };
                }
            })
            .catch(error => {
                console.error('Failed to remove vehicle:', error);
            });
    },
    addVehicleReg() {
      return this.$validator.validate('vehicleReg').then(valid => {
        if (!valid || !this.form.vehicleReg) {
          this.vehicleRegError = true;
          return Promise.reject(new Error('Vehicle registration validation failed.'));
        }
        const duplicatedVehicleReg = this.vehicles.data.find(car => car.vehicle_reg === this.form.vehicleReg.toUpperCase());
        if (duplicatedVehicleReg) {
          console.warn("Duplicate vehicle registration number.");
          this.vehicleRegError = true;
          return Promise.reject(new Error('Duplicate vehicle registration number.'));
        }

        // Add vehicle to db
        this.$store.dispatch('permits/permit/saveNewVehicle', {
          vehicle_reg: this.form.vehicleReg.toUpperCase(),
          vehicleComment: this.form.vehicleComment,
          userId: this.userId,
        })

        const vehicleData = {
          id: Date.now(), // Temporary ID
          vehicle_reg: sanitizeVehicleRegNumber(this.form.vehicleReg),
          comment: this.form.vehicleComment,
          new: true,
          permits_count: 0
        };

        this.vehicles.data.push(vehicleData);
        this.vehicles.total += 1;
        this.selectedVehicle = vehicleData;
        this.form.vehicleReg = '';
        this.form.vehicleComment = '';
        this.vehicleInputDisabled = true;
        this.vehicleRegError = false;
      });
    },
    closeModal() {
      this.$emit('close');
    },
    async confirmModal() {
      if (this.form.vehicleReg.trim() !== '') {
        try {
          await this.addVehicleReg();
          this.$emit('confirmed', this.selectedVehicle);
        } catch (error) {}
      } else if (this.selectedVehicle && this.selectedVehicle.id) {
        this.$emit('confirmed', this.selectedVehicle);
      } else {
        this.$emit('close');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/app.scss";
@import "~styles/blocks/permit-edit.scss";

.plateSearchWrapper {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  width: 97%;
}

.plateSearchInput {
  input {
    text-transform: uppercase;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .el-input__inner {
    &::placeholder {
      color: var(--black);
      text-transform: none;
    }
  }
}

.plateSearchButton {
  border-left: none;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.details-table__row--vehicle {
  display: block;
  height: 100%;
  border: none;

  .details-table__col--vehicle {
    display: flex;
    flex-direction: column;
    padding: 0;
    height: 100%;

    .el-tag {
      display: flex;
      justify-content: space-between;
      align-items: center;
      height: 40px;
      text-transform: uppercase;
    }

    .vehicle-input {
      max-width: 100%;
      height: auto;
      margin: 2px;
    }

    .el-input-group {
      margin: 2px 0;
    }
  }
}

.el-tag {
  background-color: #f5f7fa;
  border: 1px solid #f5f7fa;
  margin: 2px;
  height: 40px;
  line-height: 40px;
  padding: 0 10px 0 15px;
  color: #000;
  cursor: pointer;

  /deep/ .el-tag__close {
    color: #000;
  }

  /deep/ .el-tag__close:hover {
    background-color: transparent;
  }
}

.el-tag {
  &.el-tag--success {
    background-color: var(--brand-green);
    border: 1px solid var(--brand-green);
    color: white;

    /deep/ .el-tag__close {
      color: white;
    }

    &.el-tag--permit /deep/ .el-tag__close {
      &::before {
        background: url('~icons/number-permit.svg') center/contain no-repeat;
      }
    }
  }

  &.el-tag--permit /deep/ .el-tag__close {
    width: 30px;
    height: 100%;

    &::before {
      content: '';
      background: url('~icons/number-permit-black.svg') center/contain no-repeat;
      height: 100%;
    }
  }
}

.vehicle-input.el-button {
  position: relative;
  padding: 10px 69px 10px 15px;
  border-color: #d8dce5;
  height: auto;
  max-width: 250px;

  span {
    line-height: 20px;

    i {
      position: absolute;
      right: 0;
      top: 0;
      height: 100%;
      width: 54px;
      border-left: 1px solid #d8dce5;
      margin-left: 15px;
      vertical-align: top;
      background-color: #f5f7fa;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    i:before {
      position: absolute;
      right: 50%;
      top: 50%;
      transform: translate(50%, -50%);
      color: #97a8be;
    }
  }
}

.vehicle-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 16px;
  padding-bottom: 16px;

  &--count{
    font-size: 18px;
  }
}

.change-plate-title{
  text-align: center;
  color: var(--brand-orange);
}

.tooltip.to-bottom {
    top: auto !important;
    bottom: 50px !important;
    transform: none;
}

</style>



// WEBPACK FOOTER //
// src/components/common/VehicleSelectionModal.vue